import * as React from "react";
import Button from "@mui/material/Button";
import Dialog, { DialogProps } from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useLanguage } from "./Language";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Stack from "@mui/material/Stack";
import * as dayjs from "dayjs";
import { Dayjs } from "dayjs";
import { DateValidationError } from "@mui/x-date-pickers/models";

export const DateRangeDialogEvent = "OpenDateRangeDialog";

interface DateRangeDialogProps extends Partial<DialogProps> {
  onConfirm?: (startDate: Dayjs, endDate: Dayjs) => void;
  onClose?: () => void;
}

export const DateRangeDialog: React.FC<DateRangeDialogProps> = ({
  onConfirm,
  ...props
}) => {
  const theme = useTheme();
  const { translate } = useLanguage();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const startDefault = dayjs().subtract(1, "month");
  const endDefault = dayjs();

  const [startError, setStartError] =
    React.useState<DateValidationError | null>(null);
  const [endError, setEndError] = React.useState<DateValidationError | null>(
    null,
  );

  const [startDate, setStartDate] = React.useState<Dayjs | null>(startDefault);
  const [startMaxDate, setStartMaxDate] = React.useState<Dayjs | null>(
    endDefault,
  );
  const [endDate, setEndDate] = React.useState<Dayjs | null>(endDefault);
  const [endMinDate, setEndMinDate] = React.useState<Dayjs | null>(
    startDefault,
  );

  const startErrorMessage = React.useMemo(() => {
    switch (startError) {
      case "maxDate":
      case "minDate": {
        return translate("InvalidDates");
      }
      case "invalidDate": {
        return translate("invalidDateError");
      }
      case "disableFuture": {
        return translate("disableFutureError");
      }
      default: {
        return "";
      }
    }
  }, [startError]);

  const endErrorMessage = React.useMemo(() => {
    switch (endError) {
      case "maxDate":
      case "minDate": {
        return translate("InvalidDates");
      }
      case "invalidDate": {
        return translate("invalidDateError");
      }
      case "disableFuture": {
        return translate("disableFutureError");
      }
      default: {
        return "";
      }
    }
  }, [endError]);

  const onChangeEndDate = (newValue: Dayjs) => {
    setEndDate(newValue);
    setStartMaxDate(newValue);
  };

  const onChangeStartDate = (newValue: Dayjs) => {
    setStartDate(newValue);
    setEndMinDate(newValue);
  };

  const handleClose = () => {
    props.onClose();
    setEndDate(endDefault);
    setStartDate(startDefault);
    setEndMinDate(startDefault);
    setStartMaxDate(endDefault);
  };

  const handleConfirm = () => {
    onConfirm(startDate, endDate);
    handleClose();
  };

  return (
    <>
      <Dialog
        {...props}
        open={props.open}
        fullScreen={fullScreen}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle id="responsive-dialog-title">
          {translate("selectDateRange")}
        </DialogTitle>
        <DialogContent>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              mt: 2,
            }}
          >
            <DatePicker
              desktopModeMediaQuery={theme.breakpoints.up("md")}
              autoFocus
              value={startDate}
              maxDate={startMaxDate}
              onChange={onChangeStartDate}
              onError={(newError) => setStartError(newError)}
              slotProps={{
                textField: {
                  helperText: startErrorMessage,
                  required: true,
                },
              }}
              disableFuture
              label={translate("from")}
            />
            <DatePicker
              autoFocus
              desktopModeMediaQuery={theme.breakpoints.up("md")}
              value={endDate}
              onChange={onChangeEndDate}
              onError={(newError) => setEndError(newError)}
              slotProps={{
                textField: {
                  helperText: endErrorMessage,
                  required: true,
                },
              }}
              minDate={endMinDate}
              disableFuture
              label={translate("to")}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose}>
            {translate("cancel")}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={
              !startDate || !endDate || startError != null || endError != null
            }
            autoFocus
          >
            {translate("ok")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
